// Modules
import { useNewUser, FieldErrors } from "containers/NewUser/useNewUser";
import { useRoleManagement } from "containers/RoleManagement/useRoleManagement";
import { useConfirmModal } from "containers/NewUser/useConfirmModal";

// Components
import PageTitle from "components/molecules/PageTitle/PageTitle";
import NewUserDetails from "components/organisms/NewUser/NewUserDetails";
import RoleGroup from "components/molecules/RoleGroup/RoleGroup";
import UserRolesGroup from "components/organisms/NewUser/UserRolesGroup";
import { Head } from "components/molecules/Head/Head";
import Button from "components/atoms/Button/Button";
import Spinner from "components/atoms/Spinner/Spinner";
import Label from "components/atoms/Label/Label";
import UnauthorisedAccessError from "components/molecules/ErrorPages/UnauthorisedAccessError";

// Styles
import styles from "containers/NewUser/newUserPage.module.css";

// Constants
import { NewUser as Constants, PageTitles } from "constants/global";

// Types
import { GroupDetails } from "services/api/types";
import { GroupPermissionItem, PermissionGroup } from "containers/NewGroup/useNewGroup";

type NewUserPageComponentProps = {
	adminData: {
		firstname: string;
		lastname: string;
		username: string;
		email: string;
	};
	permissionList: PermissionGroup[];
	is2FAChecked: boolean;
	selectedRoles: Array<string>;
	selectedGroups: Array<string>;
	currentSelectedGroup: { id: string; value: string };
	isCreateUserButtonEnabled?: boolean;
	loading: boolean;
	groupsList?: Array<GroupDetails>;
	fieldErrors: FieldErrors;
	createUserInProgress: boolean;
	hasMultipleLimitRoles: boolean;
	validateFields: (arg: boolean) => void;
	handleGoBack?: () => void;
	proceedToCreateUser?: () => void;
	setCurrentSelectedGroup: (value: string, id: string) => void;
	handleRemoveSelectedGroup: (value: string) => void;
	handleAdminDataChange: (value: { id: string; value: string }) => void;
	handleEnable2FA: (value: boolean) => void;
	handleAddAnotherRole: () => void;
};

const NewUserPage = () => {
	const componentProps: NewUserPageComponentProps = useNewUser();
	const { createUserAccess } = useRoleManagement();

	const {
		adminData,
		permissionList,
		is2FAChecked,
		currentSelectedGroup,
		selectedRoles,
		selectedGroups,
		isCreateUserButtonEnabled,
		loading,
		groupsList,
		fieldErrors,
		createUserInProgress,
		hasMultipleLimitRoles,
		validateFields,
		handleGoBack,
		handleAddAnotherRole,
		setCurrentSelectedGroup,
		handleAdminDataChange,
		handleEnable2FA,
		handleRemoveSelectedGroup,
		proceedToCreateUser
	} = componentProps;
	useConfirmModal({ checked: is2FAChecked, handleEnable2FA });

	if (loading) {
		return <Spinner />;
	}

	const createUserBtnType = isCreateUserButtonEnabled ? "active" : "disabled";

	return (
		<>
			{createUserAccess ? (
				<div className={styles?.root}>
					{createUserInProgress && <Spinner type="transparent" />}
					<Head>{PageTitles.NEW_USER}</Head>
					<PageTitle showBackIcon={true} title={Constants.NEW_USER_HEADER} subTitle={Constants.NEW_USER_SUBHEADER} goBack={handleGoBack} />
					<div className={styles?.main_wrapper}>
						<div>
							<div className={styles?.step_header_wrapper}>
								<h1 className={styles?.step_header}>{Constants.STEP_1_TEXT}</h1>
								<h2 className={styles?.step_subheader}>{Constants.STEP_1_SUBHEADER}</h2>
							</div>
							<div className={styles?.content}>
								<NewUserDetails
									fieldErrors={fieldErrors}
									validateFields={validateFields}
									adminData={adminData}
									is2FAChecked={is2FAChecked}
									handleAdminDataChange={handleAdminDataChange}
									handleEnable2FA={handleEnable2FA}
								/>
							</div>
						</div>
						<div>
							<div className={styles?.step_header_wrapper}>
								<h1 className={styles?.step_header}>{Constants.STEP_2_TEXT}</h1>
								<h2 className={styles?.step_subheader}>
									<Label label={Constants.STEP_2_SUBHEADER} requiredMark />
								</h2>
							</div>
							<UserRolesGroup
								groupsList={groupsList}
								selectedGroups={selectedGroups}
								currentSelectedGroup={currentSelectedGroup}
								setCurrentSelectedGroup={setCurrentSelectedGroup}
								handleRemoveSelectedGroup={handleRemoveSelectedGroup}
								handleAddAnotherRole={handleAddAnotherRole}
							/>
							{hasMultipleLimitRoles && <p className="text-red-500 pt-2">{Constants?.MULTIPLE_APPROVAL_WARNING}</p>}
							<div className={styles?.group_wrapper}>
								{permissionList?.map((group: { title?: string; values?: Array<GroupPermissionItem> }, idx: number) => {
									const checkedRoles = group?.values?.reduce((roles: Array<string>, { perm_code }: { perm_code: string }) => {
										if (selectedRoles?.includes(perm_code)) {
											roles.push(perm_code);
										}
										return roles;
									}, []);
									return <RoleGroup readonly checkedRoles={checkedRoles} title={group?.title} values={group.values} key={`${group.title}-${idx}`} />;
								})}
							</div>
						</div>
						<div className={styles?.create_user_wrapper}>
							<Button type={createUserBtnType} onClick={proceedToCreateUser} label={Constants.CREATE_USER_TEXT} />
						</div>
					</div>
				</div>
			) : (
				<UnauthorisedAccessError />
			)}
		</>
	);
};

export default NewUserPage;
